import { createContext, useContext, useState, useEffect } from "react";

/**
 * SeoContext — single-source-of-truth bridge for per-page SEO overrides.
 *
 * WHY (2026-06-15): react-helmet-async@3.0.0 does NOT reliably dedupe
 * <meta name="description"> / og:title / og:description across SEPARATE
 * <Helmet> instances. With a global <SeoHead> default AND a per-route
 * <RouteSeo> Helmet both emitting those tags, the rendered DOM ended up
 * with TWO of each — and crawlers pick the FIRST (the homepage default),
 * which kept Google showing the homepage description / Soft 404 on route
 * pages.
 *
 * Fix: make <SeoHead> the ONLY component that emits title/description/OG.
 * Per-page components (RouteSeo, …) merely PUSH their values up through
 * this context via `useSeoOverride(...)`; SeoHead renders them. Result:
 * exactly one of each tag in the DOM, always route-specific when present.
 */
const SeoContext = createContext(null);

export const SeoProvider = ({ children }) => {
  const [seo, setSeo] = useState(null);
  return <SeoContext.Provider value={{ seo, setSeo }}>{children}</SeoContext.Provider>;
};

export const useSeoValue = () => useContext(SeoContext)?.seo || null;

/**
 * Push a per-page SEO override (or null to clear). Pass `null` when the
 * page has no override so hook order stays stable. The override is keyed
 * by its JSON so a new object identity each render does not loop.
 */
export const useSeoOverride = (override) => {
  const ctx = useContext(SeoContext);
  const setSeo = ctx?.setSeo;
  const key = JSON.stringify(override ?? null);
  useEffect(() => {
    if (!setSeo) return undefined;
    setSeo(override ?? null);
    return () => setSeo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setSeo]);
};
